import { DisposalSet, select } from '@neptune/cx/utils';

type AttachmentField = {
    addURL: string;
    authToken: string;
    contextId: string;
    deleteURL: string;
    form: string;
    maxFiles: number;
    maxSize: number;
    namespace: string;
    removeURL: string;
    stagingId: string;
};

type UploadedFile = {
    displayName: string;
    formattedSize: string;
    name: string;
    size: number;
};

type EndpointResponse = {
    errorKey?: string;
    file?: UploadedFile;
    message?: string;
    ok: boolean;
};

/**
 * Mirrors the server's allow list so an obviously wrong file is refused before it is uploaded.
 * The server's copy is the one that decides; this one only saves the round trip.
 */
const ALLOWED_EXTENSIONS = [
    'csv', 'doc', 'docx', 'gif', 'jpeg', 'jpg', 'pdf', 'png', 'ppt', 'pptx', 'rtf', 'txt', 'webp',
    'xls', 'xlsx', 'zip'
];

/** Fallbacks mirroring AttachmentPolicy, for markup that somehow arrived without the limits. */
const DEFAULT_MAX_FILES = 5;

const DEFAULT_MAX_SIZE = 10_000_000;

/** Generous enough for a 10 MB file on a slow connection, short enough to recover from a stall. */
const UPLOAD_TIMEOUT_MS = 120_000;

const withToken = (url: string, token: string) =>
    `${url}${url.includes('?') ? '&' : '?'}p_auth=${encodeURIComponent(token)}`;

const readField = (fieldEl: HTMLElement): AttachmentField => ({
    addURL: fieldEl.dataset.chroaAttachmentAddUrl ?? '',
    authToken: fieldEl.dataset.chroaAttachmentAuthToken ?? '',
    contextId: fieldEl.dataset.chroaAttachmentContextId ?? '0',
    deleteURL: fieldEl.dataset.chroaAttachmentDeleteUrl ?? '',
    form: fieldEl.dataset.chroaAttachmentForm ?? '',
    // A missing attribute must not read as a limit of zero, which would refuse every file
    // before it was ever offered to the server that actually decides.
    maxFiles: Number(fieldEl.dataset.chroaAttachmentMaxFiles) || DEFAULT_MAX_FILES,
    maxSize: Number(fieldEl.dataset.chroaAttachmentMaxSize) || DEFAULT_MAX_SIZE,
    namespace: fieldEl.dataset.chroaAttachmentNamespace ?? '',
    removeURL: fieldEl.dataset.chroaAttachmentRemoveUrl ?? '',
    stagingId: fieldEl.dataset.chroaAttachmentStagingId ?? ''
});

type LiferayHost = {
    Util?: {
        openConfirmModal?: (options: {
            message: string;
            onConfirm: (confirmed: boolean) => void;
        }) => void;
        openToast?: (options: { message: string; type: 'success' }) => void;
    };
};

const liferayUtil = () => (window as { Liferay?: LiferayHost }).Liferay?.Util;

/**
 * Liferay's modal where the page provides one, and the browser's where it does not.
 *
 * Liferay's modal answers from its close handler as well as its OK button, so a confirmation can
 * arrive as `false` followed at once by `true`. A `true` therefore wins whenever it comes, and a
 * `false` only counts as a cancel if no `true` has followed it by the next tick.
 */
const confirmThen = (message: string, onConfirm: () => void, onCancel: () => void) => {
    let confirmed = false;

    const answer = (yes: boolean) => {
        if (yes) {
            if (!confirmed) {
                confirmed = true;
                onConfirm();
            }

            return;
        }

        setTimeout(() => {
            if (!confirmed) onCancel();
        });
    };

    const openConfirmModal = liferayUtil()?.openConfirmModal;

    if (openConfirmModal) {
        openConfirmModal({ message, onConfirm: answer });
    } else {
        answer(window.confirm(message));
    }
};

/**
 * Stages files against this composer's own server-side folder as they are chosen, and names each
 * one in a hidden input so a file the member removes from the list is left behind even if the
 * removal never reached the server.
 *
 * The control is hidden in the markup and revealed here: a file input inside a form that is not
 * multipart submits the file's name and silently drops its bytes, so with no JavaScript the
 * feature must not appear at all.
 */
export const initAttachmentField = (fieldEl: HTMLElement, gc: DisposalSet) => {
    if (fieldEl.dataset.chroaAttachmentsReady) return;

    const inputEl = select<HTMLInputElement>(fieldEl, '.chroa-discussion-board__attachment-input');
    const listEl = select<HTMLElement>(fieldEl, '[data-chroa-attachment-staged]');
    const statusEl = select<HTMLElement>(fieldEl, '.chroa-discussion-board__attachment-status');
    const formEl = inputEl.form ?? fieldEl.closest('form');

    if (!formEl) return;

    const field = readField(fieldEl);
    const pending = new Set<XMLHttpRequest>();

    inputEl.accept = ALLOWED_EXTENSIONS.map((extension) => `.${extension}`).join(',');

    const submitEls = [...formEl.querySelectorAll<HTMLButtonElement>('button[type="submit"]')];

    // A member who submits while an upload is still in flight posts without the file.
    const setBusy = () => {
        const busy = pending.size > 0;

        submitEls.forEach((submitEl) => {
            submitEl.disabled = busy;
        });
    };

    // A transport failure carries no server message, and an empty status region reads as though
    // nothing went wrong -- while the row has already been taken away.
    // The region ships styled as an error, which is what it says almost every time.
    const say = (message: string | undefined, tone: 'danger' | 'neutral' | 'success' = 'danger') => {
        statusEl.classList.toggle('text-danger', tone === 'danger');
        statusEl.classList.toggle('text-success', tone === 'success');
        statusEl.textContent = message || '';
    };

    const failed = (response: EndpointResponse) =>
        say(response.message ?? fieldEl.dataset.chroaAttachmentFailedMessage);

    const countStaged = () => listEl.querySelectorAll('li').length;

    // Removing a saved file frees a slot without a reload, so this is kept current rather than read once.
    let savedCount = fieldEl.querySelectorAll(
        '.chroa-discussion-board__attachments:not([data-chroa-attachment-staged]) li'
    ).length;

    const extensionOf = (fileName: string) => {
        const index = fileName.lastIndexOf('.');

        return index < 0 ? '' : fileName.slice(index + 1).toLowerCase();
    };

    const post = (url: string, body: FormData, onProgress?: (fraction: number) => void) =>
        new Promise<EndpointResponse>((resolve) => {
            const xhr = new XMLHttpRequest();

            pending.add(xhr);
            setBusy();

            // fetch() cannot report upload progress, which is the whole point of the bar below.
            if (onProgress) {
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) onProgress(event.loaded / event.total);
                };
            }

            const settle = (response: EndpointResponse) => {
                pending.delete(xhr);
                setBusy();
                resolve(response);
            };

            xhr.onload = () => {
                try {
                    // The status code is advisory: whether a portlet resource response carries it
                    // through depends on the pipeline in front of it.
                    settle(JSON.parse(xhr.responseText) as EndpointResponse);
                } catch {
                    settle({ ok: false });
                }
            };
            xhr.onerror = () => settle({ ok: false });
            xhr.onabort = () => settle({ ok: false });
            xhr.ontimeout = () => settle({ ok: false });

            xhr.open('POST', withToken(url, field.authToken));

            // Without this a connection that stalls leaves the request pending for as long as the
            // browser allows, and the submit button disabled with it.
            xhr.timeout = UPLOAD_TIMEOUT_MS;
            xhr.send(body);
        });

    const addRow = (displayName: string) => {
        const rowEl = document.createElement('li');
        const nameEl = document.createElement('span');
        const progressEl = document.createElement('progress');

        rowEl.className = 'chroa-discussion-board__attachment';
        nameEl.textContent = displayName;
        progressEl.max = 1;
        progressEl.value = 0;

        rowEl.append(nameEl, progressEl);
        listEl.append(rowEl);

        return { nameEl, progressEl, rowEl };
    };

    const finishRow = (
        rowEl: HTMLElement, progressEl: HTMLProgressElement, uploaded: UploadedFile
    ) => {
        progressEl.remove();

        const sizeEl = document.createElement('span');
        const removeEl = document.createElement('button');
        const hiddenEl = document.createElement('input');

        sizeEl.className = 'chroa-discussion-board__attachment-size';
        sizeEl.textContent = uploaded.formattedSize;

        removeEl.className = 'btn btn-secondary btn-sm no-arrow';
        removeEl.type = 'button';
        removeEl.textContent = fieldEl.dataset.chroaAttachmentRemoveLabel ?? 'Remove';

        hiddenEl.type = 'hidden';
        hiddenEl.name = `${field.namespace}selectUploadedFile`;
        hiddenEl.value = uploaded.name;

        removeEl.addEventListener('click', () => {
            void unstage(removeEl, rowEl, uploaded.name);
        });

        rowEl.append(sizeEl, removeEl, hiddenEl);
    };

    /** Discards a file that has been staged but not yet posted, and drops its row and input. */
    const unstage = async (buttonEl: HTMLButtonElement, rowEl: HTMLElement, tempFileName: string) => {
        buttonEl.disabled = true;

        const body = new FormData();

        body.append(`${field.namespace}attachmentForm`, field.form);
        body.append(`${field.namespace}attachmentContextId`, field.contextId);
        body.append(`${field.namespace}attachmentStagingId`, field.stagingId);
        body.append(`${field.namespace}attachmentFileName`, tempFileName);

        const response = await post(field.deleteURL, body);

        if (response.ok) {
            rowEl.remove();
            say('');
        } else {
            buttonEl.disabled = false;
            failed(response);
        }
    };

    // Liferay's toast where the page provides one, and the status region where it does not.
    const toast = (message: string) => {
        const openToast = liferayUtil()?.openToast;

        if (openToast) {
            openToast({ message, type: 'success' });
        } else {
            say(message, 'success');
        }
    };

    // A member who clicked elsewhere while the request was in flight keeps their focus. A disabled
    // button drops focus to the body, so that counts as still being here.
    const hasFocusWithin = (rowEl: HTMLElement) => {
        const activeEl = document.activeElement;

        return !activeEl || activeEl === document.body || rowEl.contains(activeEl);
    };

    const removeButtonOf = (rowEl: Element | null) =>
        rowEl?.querySelector<HTMLButtonElement>('[data-chroa-attachment-remove]:not(:disabled)') ?? null;

    /**
     * @param fromKeyboard whether the removal was started from the keyboard. Only then is focus moved
     *     on: a pointer user has no use for it, and the theme paints a focused button as pressed.
     */
    const dropSavedRow = (rowEl: HTMLElement, fromKeyboard: boolean) => {
        if (!rowEl.isConnected) return;

        const savedListEl = rowEl.parentElement;
        const keepFocusNearby = fromKeyboard && hasFocusWithin(rowEl);

        // The focused button is about to leave the page, which would drop a keyboard or screen
        // reader user at the top of the document.
        const nextFocusEl = (
            removeButtonOf(rowEl.nextElementSibling) ?? removeButtonOf(rowEl.previousElementSibling) ?? inputEl
        );

        rowEl.remove();

        if (keepFocusNearby) nextFocusEl.focus();
        savedCount -= 1;

        if (savedListEl && !savedListEl.querySelector('li')) savedListEl.remove();
    };

    /** Deletes a file already saved on the post. Unlike unstaging, this changes the post immediately. */
    const removeSaved = async (buttonEl: HTMLButtonElement, rowEl: HTMLElement, fromKeyboard: boolean) => {
        const body = new FormData();

        body.append(`${field.namespace}attachmentForm`, buttonEl.dataset.chroaAttachmentRemoveForm ?? '');
        body.append(
            `${field.namespace}attachmentContextId`, buttonEl.dataset.chroaAttachmentRemoveContextId ?? ''
        );
        body.append(`${field.namespace}attachmentFileName`, buttonEl.dataset.chroaAttachmentRemoveFileName ?? '');

        const response = await post(field.removeURL, body);

        if (response.ok) {
            dropSavedRow(rowEl, fromKeyboard);
            say('');
            toast(fieldEl.dataset.chroaAttachmentRemovedMessage ?? '');
        } else if (response.errorKey === 'discussion-attachment-is-no-longer-staged') {

            // Already gone -- removed from another tab, say. The row is stale either way.
            dropSavedRow(rowEl, fromKeyboard);
            failed(response);
        } else {
            const keepFocusNearby = fromKeyboard && hasFocusWithin(rowEl);

            buttonEl.disabled = false;

            if (keepFocusNearby) buttonEl.focus();

            failed(response);
        }
    };

    const upload = async (file: File) => {
        if (countStaged() + savedCount >= field.maxFiles) {
            say(fieldEl.dataset.chroaAttachmentLimitMessage ?? '');

            return;
        }

        if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
            say(fieldEl.dataset.chroaAttachmentRejectMessage ?? '');

            return;
        }

        if (file.size > field.maxSize) {
            say(fieldEl.dataset.chroaAttachmentTooLargeMessage ?? '');

            return;
        }

        const { nameEl, progressEl, rowEl } = addRow(file.name);
        const body = new FormData();

        body.append(`${field.namespace}attachmentForm`, field.form);
        body.append(`${field.namespace}attachmentContextId`, field.contextId);
        body.append(`${field.namespace}attachmentStagingId`, field.stagingId);
        body.append(`${field.namespace}attachmentFile`, file, file.name);

        const response = await post(field.addURL, body, (fraction) => {
            progressEl.value = fraction;
        });

        if (response.ok && response.file) {
            const { displayName } = response.file;

            // A second file under a name this post already holds is staged renumbered, which the
            // row shows but a screen reader would not otherwise hear.
            nameEl.textContent = displayName;
            finishRow(rowEl, progressEl, response.file);
            say(
                displayName === file.name
                    ? ''
                    : (fieldEl.dataset.chroaAttachmentRenamedMessage ?? '').replace(
                        /\{([01])\}/g, (_, index) => (index === '0' ? file.name : displayName)
                    ),
                'neutral'
            );
        } else {
            rowEl.remove();
            failed(response);
        }
    };

    // Chained, not merely sequential within one call: the picker and a drop each start a run of
    // their own, and two runs overlapping would both read the same staged total before either had
    // added to it -- which is what the server checks an upload against before it writes. (The
    // file count survives that on its own, since the row is appended before the await.) The catch
    // keeps one thrown upload from leaving the queue rejected and silently dropping every later
    // one.
    let queue = Promise.resolve();

    const uploadAll = (files: FileList | null) => {
        queue = queue
            .then(async () => {
                for (const file of [...(files ?? [])]) {
                    await upload(file);
                }
            })
            .catch(() => {});

        return queue;
    };

    // Files staged by an earlier visit and re-rendered into a refilled form. Without this they
    // would have no remove control at all, so a member who had reached the limit could neither
    // add a file nor free a slot.
    fieldEl.querySelectorAll<HTMLButtonElement>('[data-chroa-attachment-unstage]').forEach(
        (unstageEl) => {
            const rowEl = unstageEl.closest<HTMLElement>('li');
            const tempFileName = unstageEl.dataset.chroaAttachmentUnstage ?? '';

            if (!rowEl) return;

            gc.register(unstageEl).addListener('click', () => {
                void unstage(unstageEl, rowEl, tempFileName);
            });
        }
    );

    // Files already saved on the post. Removal takes effect immediately, so it is confirmed first
    // rather than staged until save.
    fieldEl.querySelectorAll<HTMLButtonElement>('[data-chroa-attachment-remove]').forEach(
        (removeEl) => {
            const rowEl = removeEl.closest<HTMLElement>('li');

            if (!rowEl) return;

            gc.register(removeEl).addListener('click', (event) => {
                const { chroaAttachmentRemoveConfirm: message = '' } = removeEl.dataset;

                // A button activated with Enter or Space reports a click count of zero.
                const fromKeyboard = (event as MouseEvent).detail === 0;

                // Disabled from the first click, not the confirmation: a second click before the
                // modal draws would otherwise open a second one and remove the file twice.
                removeEl.disabled = true;

                confirmThen(
                    message,
                    () => {
                        void removeSaved(removeEl, rowEl, fromKeyboard);
                    },
                    () => {
                        removeEl.disabled = false;

                        if (fromKeyboard) removeEl.focus();
                    }
                );
            });
        }
    );

    gc.register(inputEl).addListener('change', () => {
        void uploadAll(inputEl.files).then(() => {
            inputEl.value = '';
        });
    });

    // Additive: the input stays the real control, so the keyboard path is unchanged.
    gc.register(fieldEl).addListener('dragover', (event) => {
        event.preventDefault();
    });

    gc.register(fieldEl).addListener('drop', (event) => {
        event.preventDefault();

        void uploadAll(event.dataTransfer?.files ?? null);
    });

    fieldEl.hidden = false;
    fieldEl.dataset.chroaAttachmentsReady = 'true';

    gc.register(() => {
        pending.forEach((xhr) => xhr.abort());
        pending.clear();
        submitEls.forEach((submitEl) => {
            submitEl.disabled = false;
        });

        // Back to the state the markup ships in. A field left visible after teardown is a file
        // input in a form that is no longer wired to upload anything.
        fieldEl.hidden = true;
        delete fieldEl.dataset.chroaAttachmentsReady;
    });
};
